/**
 * DMCA SERVICE
 * 
 * Handles all DMCA-related operations:
 * - Submitting claims
 * - Admin actions (hide, remove, credit)
 * - User notifications
 * - Action logging
 * 
 * Usage:
 * ```typescript
 * import { submitDMCAClaim, hideDMCAPhoto, logDMCAAction } from '../services/dmcaService';
 * 
 * // Submit a claim
 * const claimId = await submitDMCAClaim(formData);
 * 
 * // Admin hides a photo
 * await hideDMCAPhoto(claimId, photoUrl, adminUserId);
 * ```
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  collectionGroup
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
  DMCAClaim,
  DMCAClaimFormData,
  DMCAActionLog,
  DMCAActionType,
  ClaimStatus,
  ClaimResolution,
  DMCAStats
} from '../types/dmca';

// ============================================
// CLAIM OPERATIONS
// ============================================

/**
 * Submit a new DMCA claim (public - no auth required)
 */
export async function submitDMCAClaim(formData: DMCAClaimFormData): Promise<string> {
  const claim: Omit<DMCAClaim, 'id'> = {
    claimantName: formData.claimantName.trim(),
    claimantEmail: formData.claimantEmail.trim().toLowerCase(),
    claimantUrl: formData.claimantUrl?.trim() || '',
    photoUrl: formData.photoUrl.trim(),
    originalPhotoUrl: formData.originalPhotoUrl?.trim() || '',
    description: formData.description.trim(),
    requestedAction: formData.requestedAction,
    status: 'pending',
    submittedAt: serverTimestamp() as any,
    userNotified: false,
  };

  const docRef = await addDoc(collection(db, 'dmcaClaims'), claim);
  
  // Log the submission
  await logDMCAAction(docRef.id, 'claim_submitted', 'system', 
    `DMCA claim submitted by ${claim.claimantName} (${claim.claimantEmail})`
  );

  return docRef.id;
}

/**
 * Get all DMCA claims (admin only)
 */
export async function getAllDMCAClaims(statusFilter?: ClaimStatus): Promise<DMCAClaim[]> {
  let q = query(
    collection(db, 'dmcaClaims'),
    orderBy('submittedAt', 'desc')
  );

  if (statusFilter) {
    q = query(
      collection(db, 'dmcaClaims'),
      where('status', '==', statusFilter),
      orderBy('submittedAt', 'desc')
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as DMCAClaim));
}

/**
 * Get a single claim with its action logs
 */
export async function getDMCAClaimWithLogs(claimId: string): Promise<{
  claim: DMCAClaim;
  logs: DMCAActionLog[];
} | null> {
  const claimDoc = await getDoc(doc(db, 'dmcaClaims', claimId));
  
  if (!claimDoc.exists()) {
    return null;
  }

  const logsSnapshot = await getDocs(
    query(
      collection(db, 'dmcaActionLogs'),
      where('claimId', '==', claimId),
      orderBy('timestamp', 'desc')
    )
  );

  return {
    claim: { id: claimDoc.id, ...claimDoc.data() } as DMCAClaim,
    logs: logsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as DMCAActionLog))
  };
}

/**
 * Update claim status
 */
export async function updateClaimStatus(
  claimId: string,
  status: ClaimStatus,
  adminUserId: string,
  notes?: string
): Promise<void> {
  const updates: Record<string, any> = {
    status,
    reviewedBy: adminUserId,
    reviewedAt: serverTimestamp(),
  };

  if (status === 'resolved' || status === 'invalid') {
    updates.resolvedAt = serverTimestamp();
  }

  if (notes) {
    updates.resolutionNotes = notes;
  }

  await updateDoc(doc(db, 'dmcaClaims', claimId), updates);

  await logDMCAAction(claimId, 'claim_reviewed', adminUserId,
    `Claim status updated to: ${status}${notes ? ` - ${notes}` : ''}`
  );
}

/**
 * Resolve a claim with a specific resolution
 */
export async function resolveClaim(
  claimId: string,
  resolution: ClaimResolution,
  adminUserId: string,
  notes?: string
): Promise<void> {
  const updates: Record<string, any> = {
    status: 'resolved',
    resolution,
    resolvedAt: serverTimestamp(),
    reviewedBy: adminUserId,
  };
  
  // Only add resolutionNotes if provided (Firebase doesn't accept undefined)
  if (notes) {
    updates.resolutionNotes = notes;
  }

  await updateDoc(doc(db, 'dmcaClaims', claimId), updates);

  await logDMCAAction(claimId, 'claim_resolved', adminUserId,
    `Claim resolved: ${resolution}${notes ? ` - ${notes}` : ''}`
  );
}

// ============================================
// PHOTO ACTIONS
// ============================================

/**
 * Find which user has a photo by URL
 * Searches through all user items and creations
 */
export async function findPhotoOwner(photoUrl: string): Promise<{
  userId: string;
  username?: string;
  itemId: string;
  itemType: 'item' | 'creation';
  itemName?: string;
} | null> {
  // Search in items
  const itemsQuery = query(collectionGroup(db, 'items'));
  const itemsSnapshot = await getDocs(itemsQuery);

  for (const itemDoc of itemsSnapshot.docs) {
    const data = itemDoc.data();
    const photos = data.photos || data.swatchPhotos || data.photoUrls || [];
    
    // Check if any photo matches
    const hasPhoto = photos.some((p: any) => {
      const url = typeof p === 'string' ? p : p?.url;
      return url === photoUrl;
    });

    if (hasPhoto) {
      // Extract userId from path: users/{userId}/items/{itemId}
      const pathParts = itemDoc.ref.path.split('/');
      const userIdIndex = pathParts.indexOf('users') + 1;
      const userId = pathParts[userIdIndex];

      // Get username
      const userDoc = await getDoc(doc(db, 'users', userId));
      const username = userDoc.data()?.username || userDoc.data()?.displayName;

      return {
        userId,
        username,
        itemId: itemDoc.id,
        itemType: 'item',
        itemName: data.name || data.brand,
      };
    }
  }

  // Search in creations
  const creationsQuery = query(collectionGroup(db, 'creations'));
  const creationsSnapshot = await getDocs(creationsQuery);

  for (const docSnap of creationsSnapshot.docs) {
    const data = docSnap.data();
    const photos = data.photos || [];
    
    const hasPhoto = photos.some((p: any) => {
      const url = typeof p === 'string' ? p : p?.url;
      return url === photoUrl;
    });

    if (hasPhoto) {
      const pathParts = docSnap.ref.path.split('/');
      const userIdIndex = pathParts.indexOf('users') + 1;
      const userId = pathParts[userIdIndex];

      const userDocRef = await getDoc(doc(db, 'users', userId));
      const username = userDocRef.data()?.username || userDocRef.data()?.displayName;

      return {
        userId,
        username,
        itemId: docSnap.id,
        itemType: 'creation',
        itemName: data.title,
      };
    }
  }

  return null;
}

/**
 * Hide a photo (sets DMCA status to hidden)
 * The photo stays in the database but shouldn't be displayed
 */
export async function hidePhoto(
  claimId: string,
  photoUrl: string,
  adminUserId: string
): Promise<boolean> {
  const owner = await findPhotoOwner(photoUrl);
  
  if (!owner) {
    console.error('Could not find photo owner');
    return false;
  }

  const collectionPath = owner.itemType === 'item' ? 'items' : 'creations';
  const itemRef = doc(db, 'users', owner.userId, collectionPath, owner.itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    return false;
  }

  const data = itemDoc.data();
  const photosField = data.photos ? 'photos' : (data.swatchPhotos ? 'swatchPhotos' : 'photoUrls');
  const photos = data[photosField] || [];

  // Update the specific photo's DMCA status
  const updatedPhotos = photos.map((p: any) => {
    const url = typeof p === 'string' ? p : p?.url;
    if (url === photoUrl) {
      if (typeof p === 'string') {
        return { url: p, dmcaStatus: 'hidden', dmcaClaimId: claimId };
      } else {
        return { ...p, dmcaStatus: 'hidden', dmcaClaimId: claimId };
      }
    }
    return p;
  });

  await updateDoc(itemRef, { [photosField]: updatedPhotos });

  // Update claim with owner info
  await updateDoc(doc(db, 'dmcaClaims', claimId), {
    photoOwnerId: owner.userId,
    photoOwnerUsername: owner.username,
    photoLocation: `${owner.itemType}: ${owner.itemName}`,
    itemId: owner.itemId,
  });

  await logDMCAAction(claimId, 'photo_hidden', adminUserId,
    `Photo hidden from ${owner.username}'s ${owner.itemType}: ${owner.itemName}`
  );

  return true;
}

/**
 * Remove a photo entirely
 */
export async function removePhoto(
  claimId: string,
  photoUrl: string,
  adminUserId: string
): Promise<boolean> {
  const owner = await findPhotoOwner(photoUrl);
  
  if (!owner) {
    console.error('Could not find photo owner');
    return false;
  }

  const collectionPath = owner.itemType === 'item' ? 'items' : 'creations';
  const itemRef = doc(db, 'users', owner.userId, collectionPath, owner.itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    return false;
  }

  const data = itemDoc.data();
  const photosField = data.photos ? 'photos' : (data.swatchPhotos ? 'swatchPhotos' : 'photoUrls');
  const photos = data[photosField] || [];

  // Filter out the photo
  const updatedPhotos = photos.filter((p: any) => {
    const url = typeof p === 'string' ? p : p?.url;
    return url !== photoUrl;
  });

  await updateDoc(itemRef, { [photosField]: updatedPhotos });

  await logDMCAAction(claimId, 'photo_removed', adminUserId,
    `Photo permanently removed from ${owner.username}'s ${owner.itemType}: ${owner.itemName}`
  );

  return true;
}

/**
 * Restore a hidden photo
 */
export async function restorePhoto(
  claimId: string,
  photoUrl: string,
  adminUserId: string
): Promise<boolean> {
  const owner = await findPhotoOwner(photoUrl);
  
  if (!owner) {
    return false;
  }

  const collectionPath = owner.itemType === 'item' ? 'items' : 'creations';
  const itemRef = doc(db, 'users', owner.userId, collectionPath, owner.itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    return false;
  }

  const data = itemDoc.data();
  const photosField = data.photos ? 'photos' : (data.swatchPhotos ? 'swatchPhotos' : 'photoUrls');
  const photos = data[photosField] || [];

  // Update the photo status back to active
  const updatedPhotos = photos.map((p: any) => {
    const url = typeof p === 'string' ? p : p?.url;
    if (url === photoUrl) {
      if (typeof p === 'object') {
        const { dmcaStatus, dmcaClaimId, ...rest } = p;
        return { ...rest, dmcaStatus: 'cleared' };
      }
    }
    return p;
  });

  await updateDoc(itemRef, { [photosField]: updatedPhotos });

  await logDMCAAction(claimId, 'photo_restored', adminUserId,
    `Photo restored for ${owner.username}'s ${owner.itemType}: ${owner.itemName}`
  );

  return true;
}

/**
 * Add or update credit on a photo
 */
export async function updatePhotoCredit(
  claimId: string,
  photoUrl: string,
  creditName: string,
  creditUrl: string | undefined,
  adminUserId: string
): Promise<boolean> {
  const owner = await findPhotoOwner(photoUrl);
  
  if (!owner) {
    return false;
  }

  const collectionPath = owner.itemType === 'item' ? 'items' : 'creations';
  const itemRef = doc(db, 'users', owner.userId, collectionPath, owner.itemId);
  const itemDoc = await getDoc(itemRef);

  if (!itemDoc.exists()) {
    return false;
  }

  const data = itemDoc.data();
  const photosField = data.photos ? 'photos' : (data.swatchPhotos ? 'swatchPhotos' : 'photoUrls');
  const photos = data[photosField] || [];

  const updatedPhotos = photos.map((p: any) => {
    const url = typeof p === 'string' ? p : p?.url;
    if (url === photoUrl) {
      // Build credit object, only include creditUrl if provided
      const credit: Record<string, any> = { creditName };
      if (creditUrl) {
        credit.creditUrl = creditUrl;
      }
      
      if (typeof p === 'string') {
        return { url: p, ...credit };
      } else {
        return { ...p, ...credit };
      }
    }
    return p;
  });

  await updateDoc(itemRef, { [photosField]: updatedPhotos });

  await logDMCAAction(claimId, 'credit_updated', adminUserId,
    `Credit updated to: ${creditName}${creditUrl ? ` (${creditUrl})` : ''}`
  );

  return true;
}

// ============================================
// USER NOTIFICATIONS
// ============================================

/**
 * Notify user about DMCA action on their content
 */
export async function notifyUserAboutDMCA(
  claimId: string,
  userId: string,
  message: string,
  adminUserId: string
): Promise<void> {
  // Create in-app notification
  await addDoc(collection(db, 'users', userId, 'notifications'), {
    type: 'dmca_notice',
    title: 'Photo Usage Notice',
    message,
    link: '/collection', // Or wherever they can see their items
    data: { claimId },
    read: false,
    createdAt: serverTimestamp(),
  });

  // Update claim
  await updateDoc(doc(db, 'dmcaClaims', claimId), {
    userNotified: true,
    userNotifiedAt: serverTimestamp(),
  });

  await logDMCAAction(claimId, 'user_notified', adminUserId,
    `User notified: ${message.substring(0, 100)}...`
  );
}

// ============================================
// ACTION LOGGING
// ============================================

/**
 * Log a DMCA action
 */
export async function logDMCAAction(
  claimId: string,
  actionType: DMCAActionType,
  performedBy: string,
  details: string,
  metadata?: Record<string, any>
): Promise<string> {
  const log: Omit<DMCAActionLog, 'id'> = {
    claimId,
    actionType,
    performedBy,
    details,
    timestamp: serverTimestamp() as any,
    // Only include metadata if it's defined (Firestore rejects undefined)
    ...(metadata && { metadata }),
  };

  const docRef = await addDoc(collection(db, 'dmcaActionLogs'), log);
  return docRef.id;
}

/**
 * Get action logs for a claim
 */
export async function getClaimActionLogs(claimId: string): Promise<DMCAActionLog[]> {
  const snapshot = await getDocs(
    query(
      collection(db, 'dmcaActionLogs'),
      where('claimId', '==', claimId),
      orderBy('timestamp', 'desc')
    )
  );

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as DMCAActionLog));
}

// ============================================
// STATISTICS
// ============================================

/**
 * Get DMCA statistics for admin dashboard
 */
export async function getDMCAStats(): Promise<DMCAStats> {
  const claimsSnapshot = await getDocs(collection(db, 'dmcaClaims'));
  const claims = claimsSnapshot.docs.map(doc => doc.data() as DMCAClaim);

  const pendingClaims = claims.filter(c => c.status === 'pending' || c.status === 'reviewing').length;
  const resolvedClaims = claims.filter(c => c.status === 'resolved').length;
  const photosHidden = claims.filter(c => c.resolution === 'photo_hidden').length;
  const photosRemoved = claims.filter(c => c.resolution === 'photo_removed').length;

  // Calculate average resolution time
  const resolvedWithTimes = claims.filter(c => c.submittedAt && c.resolvedAt);
  let avgResolutionDays = 0;
  
  if (resolvedWithTimes.length > 0) {
    const totalDays = resolvedWithTimes.reduce((sum, claim) => {
      const submitted = claim.submittedAt instanceof Timestamp 
        ? claim.submittedAt.toDate() 
        : new Date(claim.submittedAt);
      const resolved = claim.resolvedAt instanceof Timestamp 
        ? claim.resolvedAt.toDate() 
        : new Date(claim.resolvedAt!);
      const days = (resolved.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24);
      return sum + days;
    }, 0);
    avgResolutionDays = totalDays / resolvedWithTimes.length;
  }

  return {
    totalClaims: claims.length,
    pendingClaims,
    resolvedClaims,
    photosHidden,
    photosRemoved,
    avgResolutionDays: Math.round(avgResolutionDays * 10) / 10,
  };
}

// ============================================
// SEARCH
// ============================================

/**
 * Search claims by email, URL, or user
 */
export async function searchDMCAClaims(searchTerm: string): Promise<DMCAClaim[]> {
  const allClaims = await getAllDMCAClaims();
  const term = searchTerm.toLowerCase();

  return allClaims.filter(claim =>
    claim.claimantEmail.toLowerCase().includes(term) ||
    claim.claimantName.toLowerCase().includes(term) ||
    claim.photoUrl.toLowerCase().includes(term) ||
    claim.photoOwnerUsername?.toLowerCase().includes(term) ||
    claim.description.toLowerCase().includes(term)
  );
}
